import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconPencil, IconTrash, IconArrowRight } from '@tabler/icons-react';
import type { DeliverObjectDto, DeliverObjectDependencyDto, PhaseDto } from '../../api/model';
import { useDeliverableDependencies, useDeleteDependency } from '../../hooks/useDeliverableDependencies';
import { useToast } from '../ui/Toast/ToastProvider';
import { getApiErrorMessage } from '../../hooks/useApiMutation';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import DependencyModal from './DependencyModal';
import { getDependencyTypeShort, formatOffsetDays } from './dependencyLabels';

import styles from './DependencySection.module.scss';

interface DependencySectionProps {
  deliverable: DeliverObjectDto;
  allDeliverables: DeliverObjectDto[];
  phases?: PhaseDto[];
}

const deliverableName = (
  id: number | undefined,
  all: DeliverObjectDto[]
): string => {
  if (id == null) return 'Unknown';
  const found = all.find(d => d.id === id);
  if (!found) return `#${id}`;
  return found.name ? found.name.replaceAll('_', ' ') : `#${id}`;
};

const DependencySection: React.FC<DependencySectionProps> = ({
  deliverable,
  allDeliverables,
  phases,
}) => {
  const { t } = useTranslation('gantt');
  const { showSuccess, showError } = useToast();
  const { dependencies, isLoading } = useDeliverableDependencies(deliverable.id);
  const deleteMutation = useDeleteDependency();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<DeliverObjectDependencyDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DeliverObjectDependencyDto | null>(null);

  const { predecessors, successors } = useMemo(() => {
    const pre: DeliverObjectDependencyDto[] = [];
    const suc: DeliverObjectDependencyDto[] = [];
    dependencies.forEach(dep => {
      if (dep.successorId === deliverable.id) pre.push(dep);
      else if (dep.predecessorId === deliverable.id) suc.push(dep);
    });
    return { predecessors: pre, successors: suc };
  }, [dependencies, deliverable.id]);

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (dep: DeliverObjectDependencyDto) => {
    setEditing(dep);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: pendingDelete.id });
      showSuccess(t('dependency.toast.removed'));
    } catch (e) {
      showError(t('dependency.toast.removeError'), getApiErrorMessage(e));
    } finally {
      setPendingDelete(null);
    }
  };

  const renderRow = (dep: DeliverObjectDependencyDto, direction: 'incoming' | 'outgoing') => {
    const otherId = direction === 'incoming' ? dep.predecessorId : dep.successorId;
    const otherName = deliverableName(otherId, allDeliverables);
    return (
      <div className={styles.row} key={dep.id ?? `${dep.predecessorId}-${dep.successorId}`}>
        <div className={styles.rowMain}>
          <span className={styles.directionLabel}>
            {direction === 'incoming' ? t('dependency.directionAfter') : t('dependency.directionBefore')}
          </span>
          <IconArrowRight size={14} className={styles.arrowIcon} />
          <span className={styles.otherName} title={otherName}>{otherName}</span>
          <span className={styles.typeTag}>{getDependencyTypeShort(dep.type)}</span>
          {dep.offsetDays ? (
            <span className={styles.offsetTag}>{formatOffsetDays(dep.offsetDays)}</span>
          ) : null}
          {dep.critical && <span className={styles.criticalTag}>{t('dependency.critical')}</span>}
        </div>
        <div className={styles.rowActions}>
          <button
            className={styles.iconButton}
            onClick={() => openEdit(dep)}
            aria-label={t('dependency.ariaEditDep')}
          >
            <IconPencil size={16} />
          </button>
          <button
            className={`${styles.iconButton} ${styles.danger}`}
            onClick={() => setPendingDelete(dep)}
            aria-label={t('dependency.ariaRemoveDep')}
          >
            <IconTrash size={16} />
          </button>
        </div>
      </div>
    );
  };

  const hasAny = predecessors.length > 0 || successors.length > 0;

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h4 className={styles.title}>{t('dependency.sectionTitle')}</h4>
        <button className={styles.addButton} onClick={openCreate}>
          <IconPlus size={16} />
          <span>{t('dependency.addButton')}</span>
        </button>
      </div>

      {isLoading ? (
        <div className={styles.empty}>{t('dependency.loading')}</div>
      ) : !hasAny ? (
        <div className={styles.empty}>{t('dependency.empty')}</div>
      ) : (
        <div className={styles.lists}>
          {predecessors.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupLabel}>{t('dependency.dependsOn')}</div>
              {predecessors.map(dep => renderRow(dep, 'incoming'))}
            </div>
          )}
          {successors.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupLabel}>{t('dependency.requiredBy')}</div>
              {successors.map(dep => renderRow(dep, 'outgoing'))}
            </div>
          )}
        </div>
      )}

      <DependencyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        deliverables={allDeliverables}
        phases={phases}
        existing={editing}
        defaultPredecessorId={editing ? undefined : deliverable.id}
        lockPredecessor={!!(editing && editing.predecessorId === deliverable.id)}
        lockSuccessor={!!(editing && editing.successorId === deliverable.id)}
        hideDeleteButton={!!editing}
        onDelete={() => setPendingDelete(editing)}
      />

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title={t('dependency.confirm.removeTitle')}
        message={t('dependency.confirm.removeMessage')}
        confirmText={t('dependency.confirm.removeConfirm')}
        cancelText={t('dependency.confirm.cancel')}
        type="danger"
      />
    </div>
  );
};

export default DependencySection;
